import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { ApiOrder, calculateOrderPayment, createOrder, getOrder, getOrderPayment, getOrderPaymentLimits, PaymentCalculation, PaymentLimits, PaymentStatus, UserProfile, UsdtNetwork } from '../api/api';
import { useApp } from '../context/AppContext';
import Button from '../components/Button';
import Card from '../components/Card';
import Input from '../components/Input';
import { clientErrorMessage } from '../utils/clientError';
import { useKycAutoRefresh } from '../hooks/useKycAutoRefresh';

const featureStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
};

const dotStyle: React.CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: 'var(--accent)',
  flexShrink: 0,
  boxShadow: '0 0 8px var(--accent)',
};

const featureTextStyle: React.CSSProperties = {
  fontSize: '0.9rem',
  color: 'var(--text-muted)',
};

const MIN_PAYMENT_RUB = 10000;
const liquidityFormatter = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });
const volumeFormatter = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

function formatAsset(value: number, asset: string) {
  return `${value.toFixed(6)} ${asset}`;
}

export function VolumeInfo({ volume, asset = 'USDT' }: { volume: number | null; asset?: string }) {
  return (
    <div className="dashboard-liquidity" aria-live="polite">
      <span className="dashboard-liquidity-label">Объём сделок</span>
      <span className="dashboard-liquidity-value mono">
        {volume == null ? '...' : `${volumeFormatter.format(Math.max(0, volume))} ${asset}`}
      </span>
    </div>
  );
}

function hasProfileQuestionnaire(user: UserProfile): boolean {
  return Boolean(
    user.lastName?.trim() &&
    user.firstName?.trim() &&
    user.birthDate?.trim() &&
    user.phone?.trim()
  );
}

function qrImageSource(value: string | null | undefined): string | null {
  const source = value?.trim();
  if (!source) return null;
  if (/^(data:image\/|https?:\/\/)/i.test(source)) return source;
  if (/^(iVBOR|\/9j\/|UklGR)/.test(source)) {
    return `data:image/png;base64,${source.replace(/\s/g, '')}`;
  }
  return null;
}

export function PaymentScreen({ order, onReset }: { order: ApiOrder; onReset: () => void }) {
  const [current, setCurrent] = useState(order);
  const [payment, setPayment] = useState<PaymentStatus | null>(null);
  const [failedQrImage, setFailedQrImage] = useState<string | null>(null);
  const { userAuth } = useApp();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const poll = async () => {
      const p = await getOrderPayment(order.id);
      setPayment(p);
      if (userAuth.token) setCurrent(await getOrder(order.id, userAuth.token));
      if (['Paid', 'Fulfilled', 'Expired', 'Cancelled', 'Failed', 'completed', 'failed', 'expired'].includes(p.status)) {
        if (intervalRef.current) clearInterval(intervalRef.current);
      }
    };
    poll().catch(() => {});
    intervalRef.current = setInterval(() => poll().catch(() => {}), 3000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [order.id, userAuth.token]);

  const paymentLink = payment?.deepLink || current.paymentDeepLink;
  const sbpQr = payment?.sbpQr || current.paymentSbpQr;
  const sbpQrImage = qrImageSource(sbpQr);
  const qrValue = paymentLink || (sbpQrImage ? null : sbpQr);
  const qrImage = sbpQrImage || payment?.qrLink || current.paymentQrUrl;
  const canShowQrImage = Boolean(qrImage && failedQrImage !== qrImage);
  const openPaymentLink = paymentLink || payment?.qrLink || current.paymentQrUrl;
  const hasQr = Boolean(qrValue || canShowQrImage);

  return (
    <div className="landing-success">
      <h2 className="success-title">Оплатите заявку</h2>
      {hasQr ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, marginTop: 24 }}>
          <div className="qr-wrapper">
            {qrValue ? (
              <QRCodeSVG value={qrValue} size={200} level="M" title="QR для оплаты" />
            ) : (
              <img
                src={qrImage!}
                alt="QR для оплаты"
                width={200}
                height={200}
                onError={() => setFailedQrImage(qrImage!)}
              />
            )}
          </div>
          {openPaymentLink && (
            <a href={openPaymentLink} className="back-link" target="_blank" rel="noopener noreferrer">Открыть оплату</a>
          )}
        </div>
      ) : qrImage && failedQrImage === qrImage ? (
        <div className="payment-error" style={{ marginTop: 24 }}>
          Не удалось загрузить QR. Откройте оплату по ссылке ниже.
          {openPaymentLink && (
            <a href={openPaymentLink} className="back-link" target="_blank" rel="noopener noreferrer">Открыть оплату</a>
          )}
        </div>
      ) : (
        <div className="qr-loading-ring" />
      )}
      <div className="success-details" style={{ marginTop: 16 }}>
        <div className="success-row"><span>Заявка</span><span className="mono">{current.id}</span></div>
        <div className="success-row"><span>Сумма</span><span className="mono">{current.rub.toLocaleString('ru')} ₽</span></div>
        <div className="success-row"><span>К получению</span><span className="mono accent">{formatAsset(current.usd, current.providerCode?.toLowerCase() === 'ton' ? 'TON' : 'USDT')}</span></div>
      </div>
      <button className="back-link" onClick={onReset}>Новая заявка</button>
    </div>
  );
}

export default function DashboardPage() {
  const { userAuth, settings, refreshSettings } = useApp();
  useKycAutoRefresh();
  const [params] = useSearchParams();
  const [rub, setRub] = useState('');
  const [network, setNetwork] = useState<UsdtNetwork>('BEP20');
  const [walletAddress, setWalletAddress] = useState('');
  const [calculation, setCalculation] = useState<PaymentCalculation | null>(null);
  const [calculationLoading, setCalculationLoading] = useState(false);
  const [limits, setLimits] = useState<PaymentLimits | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [order, setOrder] = useState<ApiOrder | null>(null);
  const referralCode = params.get('ref') || params.get('promo');
  const rubNum = parseFloat(rub) || 0;
  const minPaymentRub = limits?.minAmount ?? MIN_PAYMENT_RUB;
  const maxPaymentRub = limits?.maxAmount;
  const selectedLiquidity = settings?.liquidity ?? null;
  const turnover = settings?.turnover ?? null;
  const selectedAsset = 'USDT';
  const normalizedWalletAddress = walletAddress.trim();
  const walletAddressValid = isWalletAddressValid(network, normalizedWalletAddress);

  useEffect(() => {
    refreshSettings(network).catch(() => {});
  }, [network, refreshSettings]);

  useEffect(() => {
    if (!userAuth.token) {
      setLimits(null);
      return;
    }

    let cancelled = false;
    getOrderPaymentLimits(userAuth.token)
      .then(result => { if (!cancelled) setLimits(result); })
      .catch(() => { if (!cancelled) setLimits(null); });

    return () => { cancelled = true; };
  }, [userAuth.token]);

  useEffect(() => {
    if (!userAuth.token || rubNum < minPaymentRub || (maxPaymentRub != null && rubNum > maxPaymentRub)) {
      setCalculation(null);
      setCalculationLoading(false);
      return;
    }

    let cancelled = false;
    setCalculationLoading(true);
    const timeout = setTimeout(async () => {
      try {
        const result = await calculateOrderPayment({ rub: rubNum, network }, userAuth.token!);
        if (!cancelled) {
          setCalculation(result);
          setError('');
        }
      } catch (err: any) {
        if (!cancelled) {
          setCalculation(null);
          setError(clientErrorMessage(err, 'Не удалось рассчитать сумму. Повторите попытку позже.'));
        }
      } finally {
        if (!cancelled) setCalculationLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [rubNum, minPaymentRub, maxPaymentRub, network, userAuth.token]);

  if (!userAuth.token && referralCode) {
    return <Navigate to={`/register?ref=${encodeURIComponent(referralCode)}`} replace />;
  }

  if (!userAuth.token || !userAuth.user) {
    return <Card><Link to="/login" className="back-link">Войти</Link></Card>;
  }

  const user = userAuth.user;
  const questionnaireComplete = hasProfileQuestionnaire(user);
  const kycComplete = user.isSbpVerified || user.kycStatus === 'Approved';
  const hasSelectedNetworkLiquidity = selectedLiquidity != null && selectedLiquidity > 0;
  const hasEnoughSelectedNetworkLiquidity = calculation != null &&
    selectedLiquidity != null &&
    selectedAmount(calculation) <= selectedLiquidity;
  const canCreate = questionnaireComplete && kycComplete && walletAddressValid && rubNum >= minPaymentRub && (maxPaymentRub == null || rubNum <= maxPaymentRub) && calculation != null && !calculationLoading && hasEnoughSelectedNetworkLiquidity;
  const showCalculation = rubNum >= minPaymentRub && (maxPaymentRub == null || rubNum <= maxPaymentRub);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!userAuth.token || !canCreate) return;
    setLoading(true);
    setError('');
    try {
      setOrder(await createOrder({ rub: rubNum, network, uid: normalizedWalletAddress }, userAuth.token));
    } catch (err: any) {
      setError(clientErrorMessage(err, 'Не удалось создать платёж. Повторите попытку позже.'));
    } finally {
      setLoading(false);
    }
  }

  if (order) return <PaymentScreen order={order} onReset={() => { setOrder(null); setRub(''); setWalletAddress(''); }} />;

  return (
    <div className="landing-layout">
      <div className="landing-left">
        <h1 className="landing-headline">Покупка {selectedAsset}<br /><span className="accent">через СБП</span></h1>
        <p className="landing-sub">Быстрая оплата в рублях.<br />Статус платежа доступен в кабинете.</p>
        <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={featureStyle}><span style={dotStyle}></span><span style={featureTextStyle}>Получайте USDT в сетях BNB Chain или TON</span></div>
          <div style={featureStyle}><span style={dotStyle}></span><span style={featureTextStyle}>Пройдите KYC перед первым платежом</span></div>
          <div style={featureStyle}><span style={dotStyle}></span><span style={featureTextStyle}>Следите за заявками в личном кабинете</span></div>
        </div>
      </div>
      <div className="landing-right">
        <Card>
          <VolumeInfo volume={turnover} asset={selectedAsset} />
          <div className="dashboard-required-actions">
            <label className="dashboard-status-check">
              <input type="checkbox" checked={questionnaireComplete} readOnly aria-label="Анкета заполнена" />
              <span>Анкета заполнена</span>
            </label>
            <label className="dashboard-status-check">
              <input type="checkbox" checked={kycComplete} readOnly aria-label="KYC пройден" />
              <span>KYC пройден</span>
            </label>
          </div>
          <form onSubmit={submit}>
            <fieldset className="network-selector">
              <legend>Сеть и актив получения</legend>
              <div className="network-selector-options">
                {(['TRC20', 'BEP20', 'TONUSDT'] as UsdtNetwork[]).map(option => {
                  const disabled = option === 'TRC20';
                  const networkName = option === 'TRC20' ? 'USDT · TRON' : option === 'BEP20' ? 'USDT · BNB Chain' : 'USDT · TON';
                  return (
                    <label
                      key={option}
                      className={`network-option${network === option ? ' network-option--active' : ''}${disabled ? ' network-option--disabled' : ''}`}
                      title={disabled ? 'Пока недоступно' : undefined}
                      aria-disabled={disabled}
                    >
                      <input
                        type="radio"
                        name="network"
                        value={option}
                        checked={network === option}
                        disabled={disabled}
                        onChange={() => {
                          if (!disabled) setNetwork(option);
                        }}
                      />
                      <span>{option === 'TONUSDT' ? 'TON' : option}</span>
                      <small>{disabled ? 'Пока недоступно' : networkName}</small>
                    </label>
                  );
                })}
              </div>
              <p className="network-selector-hint">
                Выберите ту же сеть в кошельке получателя. Переводы между разными сетями не зачисляются.
              </p>
            </fieldset>
            <Input
              label={`Адрес кошелька для получения ${selectedAsset}`}
              value={walletAddress}
              onChange={e => setWalletAddress(e.target.value.trim())}
              placeholder={network === 'TRC20' ? 'T...' : network === 'TONUSDT' ? 'UQ... или EQ...' : '0x...'}
              required
              error={walletAddress && !walletAddressValid ? `Укажите корректный адрес ${network}` : undefined}
              hint="Средства будут отправлены на этот адрес после оплаты."
            />
            <Input label="Сумма в рублях" type="number" min={minPaymentRub.toString()} max={maxPaymentRub?.toString()} value={rub} onChange={e => setRub(e.target.value)} suffix="₽" required />
            {showCalculation && (
              <div className="calc-result">
                <span className="calc-label">Получите</span>
                <span className="calc-amount mono">{calculation ? formatAsset(selectedAmount(calculation), selectedAsset) : '...'}</span>
              </div>
            )}
            {rubNum > 0 && rubNum < minPaymentRub && <div style={{ color: 'var(--text-muted)', margin: '10px 0' }}>Минимальная сумма оплаты {minPaymentRub.toLocaleString('ru')} ₽</div>}
            {maxPaymentRub != null && rubNum > maxPaymentRub && <div style={{ color: 'var(--text-muted)', margin: '10px 0' }}>Максимальная сумма оплаты {maxPaymentRub.toLocaleString('ru')} ₽</div>}
            {selectedLiquidity != null && selectedLiquidity <= 0 && <div style={{ color: 'var(--danger)', margin: '10px 0' }}>В выбранной сети сейчас нет доступной ликвидности. Выберите другую сеть.</div>}
            {hasSelectedNetworkLiquidity && calculation && !hasEnoughSelectedNetworkLiquidity && <div style={{ color: 'var(--danger)', margin: '10px 0' }}>Недостаточно ликвидности в сети {network}. Доступно {liquidityFormatter.format(Math.max(0, selectedLiquidity ?? 0))} {selectedAsset}.</div>}
            {error && <div style={{ color: 'var(--danger)', margin: '10px 0' }}>{error}</div>}
            <Button type="submit" fullWidth size="lg" loading={loading || calculationLoading} disabled={!canCreate}>Создать оплату</Button>
          </form>
        </Card>
        <div className="legal-info-block">
          Операции пополнения через СБП осуществляются с использованием инфраструктуры лицензированного поставщика услуг виртуальных активов Bitbanker KG (Кыргызская Республика). Прохождение идентификации (KYC) является обязательным условием использования сервиса.
        </div>
      </div>
    </div>
  );
}

export function isWalletAddressValid(network: UsdtNetwork, address: string) {
  if (network === 'TRC20') return /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(address);
  if (network === 'TONUSDT') return /^(?:[EU]Q[A-Za-z0-9_-]{46}|-?\d+:[a-fA-F0-9]{64})$/.test(address);
  return /^0x[a-fA-F0-9]{40}$/.test(address);
}

function selectedAmount(calculation: PaymentCalculation) {
  return calculation.usdtAmount;
}
